/**
 * useTasksInbox · asignaciones pendientes del user logueado.
 *
 * Backend: SELECT FROM core.task_assignments WHERE user_id=auth.uid()
 * AND accepted_at IS NULL AND declined=false, JOIN core.tasks.
 *
 * Mutations:
 *  - accept(taskId, role) → set accepted_at = now()
 *  - decline(taskId, role) → set declined = true
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { useSupabase } from '@/hooks/useSupabase';

import * as inboxApi from '../api/taskInbox';

export const INBOX_KEY = ['core', 'tasks-inbox'] as const;

export function useTasksInbox() {
  const { session } = useSupabase();
  const userId = session?.user?.id ?? null;
  return useQuery({
    // userId en la key → no se mezcla cache entre usuarios. Las invalidaciones
    // de INBOX_KEY siguen matcheando por prefijo.
    queryKey: [...INBOX_KEY, userId],
    queryFn: () => inboxApi.listMyInbox(userId!),
    enabled: !!userId,
    staleTime: 30_000,
  });
}

export function useAcceptAssignment() {
  const qc = useQueryClient();
  const { session } = useSupabase();
  const userId = session?.user?.id;

  return useMutation({
    mutationFn: async ({ taskId, role }: { taskId: string; role: string }) => {
      if (!userId) throw new Error('No hay sesión');
      await inboxApi.acceptAssignment(taskId, userId, role);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: INBOX_KEY });
      qc.invalidateQueries({ queryKey: ['core', 'tasks'] });
      qc.invalidateQueries({ queryKey: ['core', 'v_my_due_tasks'] });
      toast.success('Asignación aceptada');
    },
    onError: (e: Error) => toast.error('No se pudo aceptar', { description: e.message }),
  });
}

export function useDeclineAssignment() {
  const qc = useQueryClient();
  const { session } = useSupabase();
  const userId = session?.user?.id;

  return useMutation({
    mutationFn: async ({
      taskId,
      role,
      reason,
    }: {
      taskId: string;
      role: string;
      reason?: string | null;
    }) => {
      if (!userId) throw new Error('No hay sesión');
      await inboxApi.declineAssignment(taskId, userId, role, reason);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: INBOX_KEY });
      toast.success('Asignación rechazada');
    },
    onError: (e: Error) => toast.error('No se pudo rechazar', { description: e.message }),
  });
}
